import React, { useCallback } from 'react';
import { FormGroup, TextArea } from '~/components/form';
import { IoMdMap } from 'react-icons/io';
import { useForm } from '~/hooks';
import { useSectionLoader, useAutoSave, useDeleteEntireSectionDetail } from '../../hooks';
import sectionTypes from '../sectionTypes';
import RecordedInBook from './RecordedInBook';
import { SectionHeader } from '../../../PolicySearchRequest/common';

/**
 * @typedef RecordedPlatValues
 * @property {number} sectionId
 * @property {string} violation
 * @property {string} accessToPublicRightOfWay
 * @property {string} recordedPlat
 * @property {string} accessType
 * @property {[object]} initialDefaultData
 * @typedef FormState
 * @property {RecordedPlatValues} values The current form values
 */

/** @type {RecordedPlatValues} */
const defaultState = {
    violation: 'No',
    accessType: '',
    recordedPlat: 'Yes',
    accessToPublicRightOfWay: '',
    orderNumber: null,
    initialDefaultData: {
        recordedPlat: 'Yes',
        violation: 'No',
        feetFromFront: '',
        feetFromRear: '',
        feetFromSide: '',
        feetFromSideStreet: '',
        easementsOthers: ''
    }
};

const RecordedPlat = ({ titleOpinionId, readOnly }) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.RECORDED_PLAT, titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadSection(values);
    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        fieldActions
    } = useForm({
        /** @type {RecordedPlatValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });

    const { saveField } = useAutoSave(sectionTypes.RECORDED_PLAT, titleOpinionId, state.values.initialDefaultData, state.values.sectionId);

    const { deleteEntireSectionDetail } = useDeleteEntireSectionDetail();

    const onBlur = (value, path, validator) => {
        saveField(value, path, state.values.initialDefaultData, state.values.sectionId);
        return fieldActions.onBlur(value, path, validator);
    };

    const onChangeHandler = async (event) => {
        await saveField(event.target.value, event.target.name, state.values.initialDefaultData, state.values.sectionId);
        fieldActions.onChange(event.target.value, event.target.name);
        if (event.target.name === 'recordedPlat' && event.target.value != 'Yes' && state.values.sectionDetails && state.values.sectionDetails.length !== 0) {
            return deleteEntireSectionDetail(state.values.sectionDetails);
        }
    };

    return (
        <div className="ml-5 mr-5">
            <div className="mb-5">
                <div>
                    <div className="z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><IoMdMap size="45%" /></div>
                        <div className="ml-2"> Recorded Plat </div>
                    </div>

                </div>
            </div>
            <div className="text-sm mb-4">Please note any information changes will overwrite any previously entered. Changes are saved automatically.</div>
            <div className="col-span-8 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-4 text-sm bold mb-4 mt-4">

                <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-4">
                    <p className="font-medium text-neutral-90">
                        Is there a Recorded Plat?
                    </p>
                    <input type="radio" value="Yes" name="recordedPlat"
                        checked={state.values.recordedPlat === 'Yes'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> Yes
                    <input type="radio" value="No" name="recordedPlat"
                        checked={state.values.recordedPlat === 'No'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> No
                    <input type="radio" value="None" name="recordedPlat"
                        checked={state.values.recordedPlat === 'None'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> None Since Prior Policy
                </div>

                {state.values.recordedPlat === 'Yes' ?
                    <div className="col-span-12 gap-x-5 gap-y-4">
                        {/* Current Owner(s) */}
                        <SectionHeader colSpan={12}> Recorded In</SectionHeader>
                        <RecordedInBook colSpan={12} fieldActions={fieldActions} titleOpinionId={titleOpinionId} sectionId={state.values.sectionId} sectionDetails={state.values.sectionDetails} initialDefaultData={state.values.initialDefaultData} editMode={!readOnly} />
                        <h5 className="col-span-12 font-semibold mt-8 mb-4"> Building Setback Lines </h5>
                        <div className="grid grid-cols-12 grid-flow-row gap-x-4">
                            <FormGroup
                                label="Feet from Front"
                                groupClassName="col-span-3"
                                path='feetFromFront'
                                readOnly={readOnly}
                                value={state.values.feetFromFront}
                                error={state.errors.feetFromFront}
                                {...fieldActions}
                                onBlur={onBlur}
                            />
                            <FormGroup
                                label="Feet from Side"
                                groupClassName="col-span-3"
                                path='feetFromSide'
                                readOnly={readOnly}
                                value={state.values.feetFromSide}
                                error={state.errors.feetFromSide}
                                {...fieldActions}
                                onBlur={onBlur}
                            />
                            <FormGroup
                                label="Feet from Side Street"
                                groupClassName="col-span-3"
                                path='feetFromSideStreet'
                                readOnly={readOnly}
                                value={state.values.feetFromSideStreet}
                                error={state.errors.feetFromSideStreet}
                                {...fieldActions}
                                onBlur={onBlur}
                            />
                            <FormGroup
                                label="Feet from Rear"
                                groupClassName="col-span-3"
                                path='feetFromRear'
                                readOnly={readOnly}
                                value={state.values.feetFromRear}
                                error={state.errors.feetFromRear}
                                {...fieldActions}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-7 mb-5 mt-2">
                            <p className="font-medium text-neutral-90">
                                Violated ?
                            </p>
                            <input type="radio" value="Yes" name="violation"
                                checked={state.values.violation === 'Yes'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> Yes
                            <input type="radio" value="No" name="violation"
                                checked={state.values.violation === 'No'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> No
                            <input type="radio" value="Unknown" name="violation"
                                checked={state.values.violation === 'Unknown'}
                                disabled={readOnly}
                                onChange={onChangeHandler}
                            /> Unknown
                        </div>

                        <TextArea
                            type="textarea"
                            label="Easements/ Other Matters on this Plat"
                            name="easementsOthers"
                            path='easementsOthers'
                            readOnly={readOnly}
                            groupClassName="col-span-12"
                            value={state.values.easementsOthers}
                            error={state.errors.easementsOthers}
                            {...fieldActions}
                            onChange={(easementsOthers) => {
                                saveField(easementsOthers, 'easementsOthers', state.values.initialDefaultData);
                                fieldActions.onChange(easementsOthers, 'easementsOthers');
                            }}
                            onBlur={onBlur}
                        />

                    </div>
                    :
                    <div></div>
                }

                <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-4">
                    <p className="font-medium text-neutral-90">
                        Access to Public Right of Way?
                    </p>
                    <input type="radio" value="Yes" name="accessToPublicRightOfWay"
                        checked={state.values.accessToPublicRightOfWay === 'Yes'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> Yes
                    <input type="radio" value="No" name="accessToPublicRightOfWay"
                        checked={state.values.accessToPublicRightOfWay === 'No'}
                        disabled={readOnly}
                        //className="disabled:bg-neutral-70"
                        onChange={onChangeHandler}
                    /> No
                </div>

                {state.values.accessToPublicRightOfWay === 'Yes' &&
                    <>
                        <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                            <input type="radio" value="Direct" name="accessType"
                                checked={state.values.accessType === 'Direct'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> Direct
                            <input type="radio" value="Over a Private Easement" name="accessType"
                                checked={state.values.accessType === 'Over a Private Easement'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> Over a Private Easement
                        </div>

                        {state.values.accessType === 'Over a Private Easement' &&
                            <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-4">
                                <p className="font-medium text-neutral-90">
                                    Has A Search been Made of Adjoining Property On Which it Crosses?
                                </p>
                                <input type="radio" value="Yes" name="searchAdjoiningProperty"
                                    checked={state.values.searchAdjoiningProperty === 'Yes'}
                                    disabled={readOnly}
                                    //className="disabled:bg-neutral-70"
                                    onChange={onChangeHandler}
                                /> Yes
                                <input type="radio" value="No" name="searchAdjoiningProperty"
                                    checked={state.values.searchAdjoiningProperty === 'No'}
                                    disabled={readOnly}
                                    //className="disabled:bg-neutral-70"
                                    onChange={onChangeHandler}
                                /> No
                            </div>
                        }
                    </>
                }

            </div>
        </div>
    );
};

export default RecordedPlat;